import { useParticipant } from './useParticipant';
import { readPermissionSeed } from '../lib/permissionSeed';

// G3. The one answer to "may this person do this?" on the frontend.
//
// Before this hook the frontend never checked a permission anywhere. Route
// protection compared a role-name string out of localStorage and the sidebar
// was assembled from role-name booleans, so a super could remove a permission
// from a role in the roles page and the menu item stayed exactly where it was
// until the server refused the click. The roles page looked like a control and
// was not one.
//
// The list comes from /aai/me, which the app already loads once for the AAI
// context, so this costs no extra request. It is mirrored to localStorage for
// the same reason the role is: the first paint of the sidebar happens before
// /aai/me resolves, and a nav that flashes items in and out reads as broken.
//
// This hides menu items and blocks routes. It is never a security control:
// every server route keeps its own check, because a permission list that
// arrives over the wire is a courtesy to the person using the app, not a
// boundary.

export interface PermissionState {
  permissions: string[];
  /** True when the caller may do this. No permission argument means the
   *  surface has no requirement, which answers true. */
  can: (permission?: string | null) => boolean;
  /** The caller holds a workspace role, so the permission table has an answer
   *  about them. */
  enrolled: boolean;
  /** False until /aai/me has resolved, so a guard can wait instead of
   *  bouncing someone off a page they are allowed to see. */
  ready: boolean;
}

export function usePermissions(): PermissionState {
  const { context, role, ready } = useParticipant();
  // A resolved context is authoritative. Before it arrives the seed from the
  // last load stands in, which is what keeps the sidebar stable on reload.
  let permissions = readPermissionSeed();
  if (context) permissions = context.permissions ?? [];

  // role_permissions is keyed by WORKSPACE ROLE, so it has nothing to say about
  // someone who holds none: the plain client shell predates the program and its
  // users were never granted anything. Answering false for them would empty a
  // working sidebar, so an unenrolled caller passes. The same rule makes a
  // failed /aai/me degrade to today's behaviour instead of a blank nav.
  const enrolled = !!role;
  const set = new Set(permissions);
  const can = (permission?: string | null): boolean => {
    if (!permission) return true;
    if (!enrolled) return true;
    return set.has(permission);
  };
  return { permissions, can, enrolled, ready };
}
